<template>
  <LoadingIndicatorComponent v-if="isLoading" />

  <div v-else-if="!preview" class="text-muted">
    The upcoming invoices could not be loaded.
  </div>

  <div v-else class="card">
    <div class="card-body">
      <div
        class="d-flex flex-wrap justify-content-between align-items-baseline gap-3 mb-3"
      >
        <div data-test="run-header">
          <h4 class="font-serif mb-1">
            Monthly fee run · {{ formatRunTime(preview.run_at) }}
          </h4>
          <div class="text-body-secondary fs-9">
            Fee items created before {{ formatRunDate(preview.cutoff) }} ·
            Invoice date {{ formatCalendarDate(preview.invoice_date) }} · Due
            {{ formatCalendarDate(preview.due_date) }}
          </div>
        </div>
        <div class="fs-9 tabular-nums" data-test="status-counts">
          {{ statusCounts }}
        </div>
      </div>

      <div v-if="preview.invoices.length === 0" class="text-muted">
        No fee items are waiting for the next run.
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm mb-0 align-middle">
          <thead>
            <tr>
              <th></th>
              <th>Company</th>
              <th>Status</th>
              <th class="text-end">Items</th>
              <th class="text-end">Net</th>
              <th class="text-end">VAT</th>
              <th class="text-end">Gross</th>
              <th>Lang</th>
              <th>Recipients</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in preview.invoices" :key="row.company_id">
              <tr
                :data-company-id="row.company_id"
                :class="{ 'opacity-50': row.status === 'held' }"
              >
                <td>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-1 text-body-secondary"
                    data-test="toggle"
                    :aria-expanded="expandedCompanyIds.has(row.company_id)"
                    :aria-label="
                      expandedCompanyIds.has(row.company_id)
                        ? 'Collapse fee items'
                        : 'Expand fee items'
                    "
                    @click="toggle(row.company_id)"
                  >
                    <FontAwesomeIcon
                      :icon="
                        expandedCompanyIds.has(row.company_id)
                          ? 'chevron-down'
                          : 'chevron-right'
                      "
                    />
                  </button>
                </td>
                <td>
                  <a
                    href="#"
                    class="fw-semibold text-decoration-none"
                    data-test="company-link"
                    @click.prevent="$emit('view:company', row.company_id)"
                    >{{ row.company_name }}</a
                  >
                </td>
                <td class="text-nowrap">
                  <span
                    v-if="statusPill(row)"
                    class="badge"
                    :class="statusPill(row)?.className"
                    data-test="status-pill"
                    >{{ statusPill(row)?.label }}</span
                  >
                </td>
                <td class="text-end tabular-nums" data-test="item-count">
                  {{ row.figures ? row.lines.length : "" }}
                </td>
                <td class="text-end text-nowrap tabular-nums" data-test="net">
                  {{ netText(row) }}
                </td>
                <td class="text-end text-nowrap tabular-nums" data-test="vat">
                  {{ vatText(row) }}
                </td>
                <td
                  class="text-end text-nowrap tabular-nums fw-semibold"
                  data-test="gross"
                >
                  {{ grossText(row) }}
                </td>
                <td class="text-uppercase">{{ row.language }}</td>
                <td class="fs-9">
                  <div v-for="recipient in row.recipients" :key="recipient">
                    {{ recipient }}
                  </div>
                </td>
                <td class="text-end">
                  <span
                    :title="
                      preview.can_edit
                        ? undefined
                        : 'Only super admins can change this.'
                    "
                  >
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      data-test="hold"
                      :disabled="!preview.can_edit || isUpdatingHold"
                      @click="toggleHold(row)"
                    >
                      {{ row.status === "held" ? "Release" : "Hold" }}
                    </button>
                  </span>
                </td>
              </tr>

              <tr v-if="expandedCompanyIds.has(row.company_id)">
                <td colspan="10" class="bg-body-highlight px-5 py-2">
                  <div v-if="row.lines.length === 0" class="text-muted fs-9">
                    The fee items could not be previewed for this company.
                  </div>
                  <table
                    v-else
                    class="table table-sm table-borderless mb-0 fs-9"
                  >
                    <thead>
                      <tr>
                        <th>Created</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th class="text-end">Amount</th>
                        <th class="text-end">Unit price</th>
                        <th class="text-end">VAT</th>
                        <th class="text-end">Net</th>
                        <th class="text-end">Gross</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="line in row.lines"
                        :key="line.invoice_item_id"
                        data-test="line"
                      >
                        <td class="text-body-secondary text-nowrap">
                          {{
                            line.created_at
                              ? formatRunDate(line.created_at)
                              : ""
                          }}
                        </td>
                        <td class="text-nowrap">
                          {{ formatItemType(line.item_type) }}
                        </td>
                        <td class="text-body-secondary">
                          {{ line.description }}
                        </td>
                        <td class="text-end tabular-nums">
                          {{ formatNumberWithSpaces(line.amount) }}
                        </td>
                        <td class="text-end text-nowrap tabular-nums">
                          {{
                            formatMoneyStringWithCurrency(
                              line.unit_price,
                              line.currency
                            )
                          }}
                        </td>
                        <td class="text-end tabular-nums">
                          {{ formatVatRate(line.vat_fraction) }}
                        </td>
                        <td class="text-end text-nowrap tabular-nums">
                          {{
                            formatMoneyStringWithCurrency(
                              line.net,
                              line.currency
                            )
                          }}
                        </td>
                        <td class="text-end text-nowrap tabular-nums">
                          {{
                            formatMoneyStringWithCurrency(
                              line.gross,
                              line.currency
                            )
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr
              v-for="total in preview.totals"
              :key="total.currency"
              class="fw-semibold"
              data-test="total-row"
            >
              <td></td>
              <td>To send</td>
              <td></td>
              <td class="text-end tabular-nums">{{ total.item_count }}</td>
              <td class="text-end text-nowrap tabular-nums">
                {{ formatMoneyStringWithCurrency(total.net, total.currency) }}
              </td>
              <td class="text-end text-nowrap tabular-nums">
                {{ formatMoneyStringWithCurrency(total.vat, total.currency) }}
              </td>
              <td class="text-end text-nowrap tabular-nums">
                {{ formatMoneyStringWithCurrency(total.gross, total.currency) }}
              </td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="text-body-secondary fs-9 mt-3">
        A held company stays out of every monthly run until released. Its fee
        items remain pending for manual settlement.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatItemType } from "@/admin/invoicing/utils";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatMoneyStringWithCurrency,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { countBy } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import { upcomingInvoicesApi } from "./upcomingInvoicesApi";
import type {
  UpcomingFeeInvoice,
  UpcomingFeeInvoiceSkipReason,
  UpcomingFeeInvoices,
} from "./upcomingInvoicesTypes";

/**
 * Previews the next monthly fee invoicing run and lets a super admin hold a company out of it.
 * Rows, their order and the per-currency totals come from the server as they are: nothing is
 * re-sorted or summed here, so the tab can never disagree with the run. `can_edit` is the caller's
 * effective ceiling as the server sees it, so a sales admin gets the buttons disabled, not a 403.
 */

interface StatusPill {
  label: string;
  className: "pill-attention" | "pill-inactive";
}

defineEmits<{
  "view:company": [companyId: number];
}>();

// The run's dates are Tallinn wall-clock times (the beat schedule's timezone), whatever the viewer's.
const SOLDERA_TIMEZONE = "Europe/Tallinn";

const SKIP_LABELS: Record<UpcomingFeeInvoiceSkipReason, string> = {
  mixed_vat_rates: "Skip · mixed VAT rates",
  too_many_merit_rows: "Skip · over 200 Merit rows",
  non_positive_total: "Skip · total not positive",
};

const runDateFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: SOLDERA_TIMEZONE,
});

const runTimeFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: SOLDERA_TIMEZONE,
});

// A calendar date names its own day: `new Date("YYYY-MM-DD")` is UTC midnight, so read it in UTC.
const calendarDateFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const { globalErrorHandler } = useGlobalHandlers();

const preview = ref<UpcomingFeeInvoices | null>(null);
const isLoading = ref(true);
const expandedCompanyIds = ref<ReadonlySet<number>>(new Set());
const isUpdatingHold = ref(false);

const statusCounts = computed(() => {
  const counts = countBy(preview.value?.invoices ?? [], "status");
  const parts = [
    `${counts.send ?? 0} to send`,
    `${counts.skipped ?? 0} skipped`,
    `${counts.held ?? 0} held`,
  ];
  return [...parts, ...(counts.error ? [`${counts.error} error`] : [])].join(
    " · "
  );
});

onMounted(async () => {
  try {
    await load();
  } finally {
    isLoading.value = false;
  }
});

const load = async () => {
  try {
    preview.value = (await upcomingInvoicesApi.get()).data;
  } catch (error: unknown) {
    // A preview that failed to refresh no longer shows what the run will do, so it is not kept.
    preview.value = null;
    globalErrorHandler(error);
  }
};

const toggle = (companyId: number) => {
  const next = new Set(expandedCompanyIds.value);
  if (!next.delete(companyId)) {
    next.add(companyId);
  }
  expandedCompanyIds.value = next;
};

const toggleHold = async (row: UpcomingFeeInvoice) => {
  isUpdatingHold.value = true;
  try {
    await upcomingInvoicesApi.setHold(row.company_id, row.status !== "held");
    await load();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isUpdatingHold.value = false;
  }
};

const statusPill = (row: UpcomingFeeInvoice): StatusPill | null => {
  switch (row.status) {
    case "skipped":
      return {
        label: SKIP_LABELS[row.skip_reason],
        className: "pill-attention",
      };
    case "error":
      return { label: "Error", className: "pill-attention" };
    case "held":
      return { label: "Held", className: "pill-inactive" };
    case "send":
      // The invoice is still created and registered with Merit; only the email goes to nobody.
      return row.recipients.length > 0
        ? null
        : { label: "No email · no owner", className: "pill-attention" };
  }
};

// An error row, or a held row whose figures could not be computed, carries no figures.
const netText = ({ figures }: UpcomingFeeInvoice) =>
  figures ? formatMoneyStringWithCurrency(figures.net, figures.currency) : "";

const vatText = ({ figures }: UpcomingFeeInvoice) => {
  if (!figures) {
    return "";
  }
  if (!figures.vat) {
    return "Mixed";
  }
  return `${formatVatRate(figures.vat.fraction)} · ${formatMoneyStringWithCurrency(figures.vat.amount, figures.currency)}`;
};

const grossText = ({ figures }: UpcomingFeeInvoice) => {
  if (!figures) {
    return "";
  }
  return figures.vat
    ? formatMoneyStringWithCurrency(figures.vat.gross, figures.currency)
    : "—";
};

// A rate, not an amount: the float round-trip is harmless, and two decimals keep a half-percent rate.
const formatVatRate = (vatFraction: string) =>
  `${Number((Number(vatFraction) * 100).toFixed(2))}%`;

const formatRunDate = (timestamp: string) =>
  runDateFormat.format(new Date(timestamp));

const formatRunTime = (timestamp: string) =>
  runTimeFormat.format(new Date(timestamp));

const formatCalendarDate = (date: string) =>
  calendarDateFormat.format(new Date(date));
</script>

<style lang="scss" scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
